import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import { useFilePreview } from '../hooks/useFilePreview';
import {
    DEFAULT_ADVANCE_LOAN_ENTRY_CATEGORIES,
    DEFAULT_LOAN_ENTRY_CATEGORY as DEFAULT_CATEGORY,
    ADVANCE_REPAYMENT_CATEGORY,
    LOAN_ENTRY_CATEGORIES,
    LOAN_SUBJECT_TYPE,
    LOAN_SUBJECT_TYPES,
    REPAYMENT_CATEGORY,
    RENTAL_CATEGORY,
    getLoanEntrySubjectType,
    getActiveLoanEntries,
    getLoanFinancialSummary,
    getLoanRepayments,
    getRepaymentSubjectType,
    isRentalLoanEntry,
    isRentalRepayment,
    normalizeLoanEntryCategory,
    normalizeLoanSubjectType
} from '../constants/loanCategories';

// 可复用的组件
const DetailItem = ({ label, value, isCurrency = false }) => (
    <div className="flex justify-between py-2 border-b">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className="text-sm text-gray-900">{value}{isCurrency ? ' 円' : ''}</dd>
    </div>
);
const EditItem = ({ label, name, value, onChange, type = 'text' }) => {
    // Set date range limits for date inputs to restrict year to 4 digits
    const dateProps = type === 'date' ? {
        min: '1900-01-01',
        max: '2099-12-31'
    } : {};

    return (
        <div>
            <label htmlFor={name} className="block text-sm font-medium text-gray-700">{label}</label>
            <input
                type={type}
                name={name}
                id={name}
                value={value || ''}
                onChange={onChange}
                {...dateProps}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-500 focus:border-gray-500"
            />
        </div>
    );
};
const SelectItem = ({ label, id = null, name, value, onChange, options }) => (
    <div>
        <label htmlFor={id || name} className="block text-sm font-medium text-gray-700">{label}</label>
        <select
            id={id || name}
            name={name}
            value={value}
            onChange={onChange}
            className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-gray-500 focus:outline-none focus:ring-gray-500"
        >
            {options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    </div>
);
const isImage = (fileType) => fileType.startsWith('image/');

const addMonthsEndOfMonth = (dateStr, months = 10) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const base = new Date(Date.UTC(year, month - 1, day));
    const targetMonthIndex = base.getUTCMonth() + months;
    const targetYear = base.getUTCFullYear() + Math.floor(targetMonthIndex / 12);
    const targetMonth = ((targetMonthIndex % 12) + 12) % 12;
    const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
    const safeDay = Math.min(day, lastDay);
    return `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(safeDay).padStart(2, '0')}`;
};

const getAttachmentLoanEntryId = (attachment) => attachment?.loanEntryId || attachment?.LoanEntryId || null;
const toNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
};
const formatDateOnly = (value) => value ? String(value).split('T')[0] : 'N/A';
const formatYen = (value) => `${toNumber(value).toLocaleString('ja-JP')} 円`;
const subjectTypeOptions = LOAN_SUBJECT_TYPES.map((subjectType) => ({
    value: subjectType,
    label: subjectType,
}));
const getRepaymentLoanEntryId = (repayment) => repayment?.loanEntryId || repayment?.LoanEntryId || null;
const getEntryId = (entry) => entry?.id || entry?.Id;
const getRentalEntryLabel = (entry) => {
    const month = formatDateOnly(entry?.loanDate);
    return month !== 'N/A' ? `${month.slice(0, 7)} 賃貸` : '賃貸';
};
const getEntryLabel = () => '賃貸';
const getAdvanceEntryLabel = (entry, index) => {
    const category = normalizeLoanEntryCategory(entry?.category ?? entry?.Category);
    const suffix = index < 26 ? String.fromCharCode(65 + index) : String(index + 1);
    return `${category}${suffix}`;
};
const getLoanCategoryTotals = (loan) => {
    const summary = getLoanFinancialSummary(loan);
    return { rental: summary.rentalBorrowed, advance: summary.advanceBorrowed };
};
const getCategoryName = (category) => (
    typeof category === 'string'
        ? category
        : category?.name ?? category?.Name ?? ''
);
const buildAdvanceCategoryOptions = (categories = []) => {
    const names = [
        ...DEFAULT_ADVANCE_LOAN_ENTRY_CATEGORIES,
        ...categories.map(getCategoryName)
    ];
    const seen = new Set();

    return names
        .map(normalizeLoanEntryCategory)
        .filter(category => category && category !== RENTAL_CATEGORY)
        .filter(category => {
            if (seen.has(category)) return false;
            seen.add(category);
            return true;
        });
};
const panelShellClass = 'rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.06)] ring-1 ring-white';
const formCardClass = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm ring-1 ring-slate-100';
const listShellClass = 'rounded-xl border border-slate-200 bg-slate-100/70 p-3 shadow-inner';
const recordCardClass = 'rounded-xl border border-slate-200 bg-white p-4 text-sm shadow-[0_10px_28px_rgba(15,23,42,0.07)]';
const attachmentAreaClass = 'mt-4 rounded-lg border border-dashed border-slate-300 bg-slate-50/90 p-3';
const getSubjectToneClass = (subjectType) => (
    normalizeLoanSubjectType(subjectType) === LOAN_SUBJECT_TYPE.CORPORATE
        ? 'border-indigo-200 bg-indigo-50 text-indigo-700'
        : 'border-slate-200 bg-slate-100 text-slate-700'
);
const FilePlaceholderIcon = () => (
    <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        className="h-5 w-5 text-slate-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
        <path d="M14 2v5h5" />
        <path d="M9 13h6" />
        <path d="M9 17h4" />
    </svg>
);
const SubjectTypeBadge = ({ subjectType, className = '' }) => {
    const normalized = normalizeLoanSubjectType(subjectType);

    return (
        <span className={`rounded-full border px-2.5 py-1 text-xs font-medium ${getSubjectToneClass(normalized)} ${className}`}>
            {normalized}
        </span>
    );
};

export default function LoanInfoModal({ isOpen, onClose, talent, showBackButton = false, onBack }) {
    const { t } = useTranslation();
    const attachmentPreview = useFilePreview(apiService.downloadAttachment);
    const [loanData, setLoanData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [loanEntryCategories, setLoanEntryCategories] = useState(DEFAULT_ADVANCE_LOAN_ENTRY_CATEGORIES);

    // 视图切换状态
    const [viewMode, setViewMode] = useState('current'); // 'current' or 'history'
    const [allLoans, setAllLoans] = useState([]);
    const [selectedHistoryLoan, setSelectedHistoryLoan] = useState(null);

    // 附件相关状态
    const [existingAttachments, setExistingAttachments] = useState([]);
    const [thumbnailUrls, setThumbnailUrls] = useState({});
    const normalizeLoan = useCallback((data) => {
        if (!data) return null;
        return {
            ...data,
            loanEntries: getActiveLoanEntries(data)
        };
    }, []);

    const fetchAttachments = useCallback(async (talentId) => {
        if (!talentId) return;
        try {
            const attachmentsData = await apiService.getAttachmentsForTalent(talentId);
            const loanAttachments = attachmentsData.filter(att => att.sourceModule === "Loan");
            setExistingAttachments(loanAttachments);

            const newThumbnailUrls = {};
            for (const att of loanAttachments) {
                if (isImage(att.fileType)) {
                    const blob = await apiService.downloadAttachment(att.id);
                    newThumbnailUrls[att.id] = URL.createObjectURL(blob);
                }
            }
            setThumbnailUrls(newThumbnailUrls);
        } catch (err) {
            setError(t('loadFailed') + ': ' + err.message);
        }
    }, [t]);

    const fetchLoanDetails = useCallback(async () => {
        if (talent?.id) {
            setIsLoading(true);
            setError('');
            try {
                // 先尝试直接获取借款信息
                try {
                    const loanData = await apiService.getLoanByTalentId(talent.id);
                    setLoanData(normalizeLoan(loanData));
                } catch (loanErr) {
                    // 如果直接获取失败，通过人才信息获取（向后兼容）
                    console.warn('Direct loan info fetch failed, trying via talent info:', loanErr.message);
                    const fullTalentData = await apiService.getTalentById(talent.id);
                    const loans = fullTalentData.loans || [];
                    const activeLoan = loans.find(l => l.status === 'Active') || loans[0];
                    setLoanData(normalizeLoan(activeLoan));
                }
            } catch (err) {
                setError(t('fetchFailed') + ': ' + err.message);
                setLoanData(null);
            } finally {
                setIsLoading(false);
            }
        }
    }, [talent, t, normalizeLoan]);

    const fetchAllLoans = useCallback(async () => {
        if (talent?.id) {
            setIsLoading(true);
            setError('');
            try {
                const loans = await apiService.getAllLoansByTalentId(talent.id);
                setAllLoans(loans || []);
            } catch (err) {
                setError(t('fetchFailed') + ': ' + err.message);
                setAllLoans([]);
            } finally {
                setIsLoading(false);
            }
        }
    }, [talent, t]);

    const fetchLoanEntryCategories = useCallback(async () => {
        try {
            const categories = await apiService.getLoanEntryCategories();
            setLoanEntryCategories(buildAdvanceCategoryOptions(categories));
        } catch (err) {
            console.warn('[LoanInfoModal] Failed to fetch loan entry categories:', err?.message);
            setLoanEntryCategories(DEFAULT_ADVANCE_LOAN_ENTRY_CATEGORIES);
        }
    }, []);

    const handleCreateLoanEntryCategory = useCallback(async (name) => {
        const created = await apiService.createLoanEntryCategory(name);
        const categories = await apiService.getLoanEntryCategories();
        setLoanEntryCategories(buildAdvanceCategoryOptions(categories));
        return normalizeLoanEntryCategory(getCategoryName(created) || name);
    }, []);

    useEffect(() => {
        if (isOpen && talent) {
            setError('');
            setViewMode('current');
            setSelectedHistoryLoan(null);
            fetchLoanEntryCategories();
            fetchLoanDetails();
            fetchAttachments(talent.id);
        }
    }, [isOpen, talent, fetchLoanDetails, fetchAttachments, fetchLoanEntryCategories]);

    useEffect(() => {
        if (viewMode === 'history' && talent) {
            fetchAllLoans();
        }
    }, [viewMode, talent, fetchAllLoans]);

    // Cleanup thumbnails when component unmounts or thumbnails change
    useEffect(() => {
        return () => {
            Object.values(thumbnailUrls).forEach(URL.revokeObjectURL);
        };
    }, [thumbnailUrls]);

    const handleDeleteAttachment = async (attachmentId) => {
        if (window.confirm(t('confirmDelete'))) {
            try {
                await apiService.deleteAttachment(attachmentId);
                fetchAttachments(talent.id); // Refresh attachments list
            } catch (err) {
                setError(t('deleteFailed') + ': ' + err.message);
            }
        }
    };

    const handleDownload = async (attachmentId, fileName) => {
        try {
            const blob = await apiService.downloadAttachment(attachmentId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url); // Clean up the object URL
        } catch (err) {
            setError(err.message || t('downloadFailed'));
        }
    };

    const handleUploadEntryAttachments = async (entryId, files) => {
        const uploadFiles = Array.from(files || []);
        if (uploadFiles.length === 0) {
            return { successCount: 0, failureCount: 0, totalCount: 0 };
        }

        const results = await Promise.allSettled(
            uploadFiles.map(file => apiService.uploadAttachment(talent.id, file, "Loan", entryId))
        );
        const successCount = results.filter(result => result.status === 'fulfilled').length;
        const failureCount = results.length - successCount;

        if (successCount > 0) {
            await fetchAttachments(talent.id);
        }

        if (failureCount > 0) {
            const firstError = results.find(result => result.status === 'rejected')?.reason;
            const message = `書類アップロード: ${successCount}/${results.length} 件成功、${failureCount} 件失敗しました`;
            setError(`${message}${firstError?.message ? ` (${firstError.message})` : ''}`);
            const error = new Error(message);
            error.uploadResult = { successCount, failureCount, totalCount: results.length };
            throw error;
        }

        return { successCount, failureCount, totalCount: results.length };
    };

    const handleCompleteLoan = async () => {
        if (!window.confirm(t('confirmMarkAsCompleted'))) return;
        try {
            await apiService.completeLoan(talent.id);
            alert(t('markCompletedSuccess'));
            fetchLoanDetails();
        } catch (err) {
            setError(t('markCompletedFailed') + ': ' + err.message);
        }
    };

    if (!isOpen) return null;

    return (
        <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-3 sm:p-5">
            <div className="bg-white rounded-lg shadow-xl w-[96vw] max-w-[1600px] max-h-[92vh] flex flex-col">
                <div className="p-6 border-b shrink-0">
                    <h2 className="text-2xl font-bold mb-4">{talent.fullName} - {t('loanInfo')}</h2>
                    {/* 视图切换按钮 */}
                    <div className="flex gap-2">
                        <button
                            onClick={() => setViewMode('current')}
                            className={`px-4 py-2 rounded-md ${
                                viewMode === 'current'
                                    ? 'bg-gray-900 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {t('activeLoan')}
                        </button>
                        <button
                            onClick={() => setViewMode('history')}
                            className={`px-4 py-2 rounded-md ${
                                viewMode === 'history'
                                    ? 'bg-gray-900 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {t('loanHistory')}
                        </button>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto bg-slate-100 p-6">
                    {isLoading ? <p>{t('loading')}</p> : (
                        viewMode === 'current' ? (
                            <div className="space-y-8">
                                <LoanSplitSection
                                    loan={loanData}
                                    talentId={talent.id}
                                    onUpdate={fetchLoanDetails}
                                    attachments={existingAttachments}
                                    thumbnailUrls={thumbnailUrls}
                                    loanEntryCategories={loanEntryCategories}
                                    onCreateLoanEntryCategory={handleCreateLoanEntryCategory}
                                    onDeleteAttachment={handleDeleteAttachment}
                                    onDownloadAttachment={handleDownload}
                                    onPreviewAttachment={attachmentPreview.openPreview}
                                    onUploadEntryAttachments={handleUploadEntryAttachments}
                                    onCompleteLoan={handleCompleteLoan}
                                />
                            </div>
                        ) : (
                            <LoanHistoryView
                                loans={allLoans}
                                selectedLoan={selectedHistoryLoan}
                                onSelectLoan={setSelectedHistoryLoan}
                            />
                        )
                    )}
                </div>
                {error && <p className="px-6 pb-4 text-sm text-gray-700 text-center">{error}</p>}
                <div className="bg-white px-6 py-3 flex justify-between border-t border-gray-200 shrink-0">
                    <div>
                        {showBackButton && (
                            <button
                                onClick={onBack}
                                className="bg-gray-900 text-white px-4 py-2 rounded-md hover:bg-gray-700"
                            >
                                ← {t('backToList')}
                            </button>
                        )}
                    </div>
                    <div>
                        <button onClick={onClose} className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50">{t('close')}</button>
                    </div>
                </div>
            </div>
        </div>
        <AttachmentPreviewModal
            previewState={attachmentPreview.previewState}
            onClose={attachmentPreview.closePreview}
        />
        </>
    );
}

function LoanAttachmentList({ attachments, thumbnailUrls, onDelete, onDownload, onPreview, compact = false }) {
    const { t } = useTranslation();

    if (!attachments || attachments.length === 0) {
        return <p className="text-xs text-gray-500">{t('none')}</p>;
    }

    return (
        <ul className={compact ? 'space-y-2' : 'grid grid-cols-1 gap-3'}>
            {attachments.map(att => (
                <li key={att.id} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                    <div className="flex items-center gap-3">
                        {isImage(att.fileType) && thumbnailUrls[att.id] ? (
                            <img src={thumbnailUrls[att.id]} alt={att.fileName} className="h-10 w-10 rounded-md object-cover ring-1 ring-slate-200" />
                        ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-xs text-slate-500 ring-1 ring-slate-200">
                                <FilePlaceholderIcon />
                                <span className="sr-only">{att.fileName}</span>
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{att.fileName}</p>
                            <p className="text-xs text-gray-500 truncate">{att.fileType}</p>
                        </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => onPreview(att.id, att.fileName, att.fileType)}
                            className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1.5 rounded hover:bg-blue-100 transition-colors"
                        >
                            {t('viewOnline')}
                        </button>
                        <button
                            type="button"
                            onClick={() => onDownload(att.id, att.fileName)}
                            className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1.5 rounded hover:bg-blue-100 transition-colors"
                        >
                            {t('downloadAttachment')}
                        </button>
                        <button
                            type="button"
                            onClick={() => onDelete(att.id)}
                            className="text-xs bg-red-50 text-red-700 px-2.5 py-1.5 rounded hover:bg-red-100 transition-colors"
                        >
                            {t('deleteAttachment')}
                        </button>
                    </div>
                </li>
            ))}
        </ul>
    );
}

// Loan History View Component
function LoanHistoryView({ loans, selectedLoan, onSelectLoan }) {
    const { t } = useTranslation();

    const formatDate = (value) => value ? value.split('T')[0] : 'N/A';

    if (loans.length === 0) {
        return (
            <div className="text-center py-8">
                <p className="text-gray-500">{t('noLoanHistory')}</p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {selectedLoan ? (
                <div>
                    <button
                        onClick={() => onSelectLoan(null)}
                        className="mb-4 text-gray-700 hover:underline"
                    >
                        ← {t('backToList')}
                    </button>
                    <LoanDetailView loan={selectedLoan} />
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {loans.map((loan) => {
                        const { rental, advance } = getLoanCategoryTotals(loan);
                        return (
                        <div
                            key={loan.id}
                            onClick={() => onSelectLoan(loan)}
                            className={`border rounded-lg p-4 cursor-pointer transition-shadow hover:shadow-lg ${
                                loan.status === 'Active' ? 'border-gray-400 bg-white' : 'border-gray-200 bg-white'
                            }`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-semibold text-lg">
                                    {t('loanInfo')} #{loan.id}
                                </h3>
                                <span
                                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                                        loan.status === 'Active'
                                            ? 'bg-gray-900 text-white'
                                            : 'bg-gray-600 text-white'
                                    }`}
                                >
                                    {loan.status === 'Active' ? t('active') : t('completed')}
                                </span>
                            </div>
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('totalAmount')}:</span>
                                    <span className="font-semibold">{loan.totalAmount} 円</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('rentalLoanAmount')}:</span>
                                    <span>{formatYen(rental)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('advanceLoanAmount')}:</span>
                                    <span>{formatYen(advance)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('loanDate')}:</span>
                                    <span>{formatDate(loan.loanDate)}</span>
                                </div>
                                {loan.status === 'Completed' && loan.completedAt && (
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">{t('completedDate')}:</span>
                                        <span>{formatDate(loan.completedAt)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between">
                                    <span className="text-gray-600">{t('remainingBalance')}:</span>
                                    <span className={loan.remainingBalance <= 0 ? 'text-gray-900 font-semibold' : ''}>
                                        {loan.remainingBalance} 円
                                    </span>
                                </div>
                            </div>
                            <div className="mt-3 text-xs text-gray-700 hover:underline">
                                {t('viewDetails')} →
                            </div>
                        </div>
                    );
                    })}
                </div>
            )}
        </div>
    );
}

// Loan Detail View Component (for history)
function LoanDetailView({ loan }) {
    const { t } = useTranslation();
    const formatDate = (value) => value ? value.split('T')[0] : 'N/A';
    const { rental, advance } = getLoanCategoryTotals(loan);

    return (
        <div className="space-y-6">
            {/* Status Badge */}
            <div className="flex items-center gap-3">
                <h3 className="text-xl font-bold">{t('loanInfo')} #{loan.id}</h3>
                <span
                    className={`px-3 py-1 rounded-full text-sm font-medium ${
                        loan.status === 'Active'
                            ? 'bg-gray-900 text-white'
                            : 'bg-gray-600 text-white'
                    }`}
                >
                    {loan.status === 'Active' ? t('active') : t('completed')}
                </span>
            </div>

            {/* Summary Information */}
            <div className="bg-white rounded-lg p-4 space-y-2 border border-gray-200">
                <DetailItem label={t('rentalLoanAmount')} value={rental} isCurrency />
                <DetailItem label={t('advanceLoanAmount')} value={advance} isCurrency />
                <DetailItem label={t('totalAmount')} value={loan.totalAmount} isCurrency />
                <DetailItem label={t('loanDate')} value={formatDate(loan.loanDate)} />
                <DetailItem label={t('repaymentStartDate')} value={formatDate(loan.repaymentStartDate)} />
                <DetailItem label={t('plannedRepaymentEndDate')} value={formatDate(loan.repaymentEndDate)} />
                {loan.status === 'Completed' && loan.completedAt && (
                    <DetailItem label={t('completedDate')} value={formatDate(loan.completedAt)} />
                )}
                <DetailItem label={t('remainingBalance')} value={loan.remainingBalance} isCurrency />
            </div>

            {/* Loan Entries */}
            <div>
                <h4 className="font-semibold text-md mb-3">{t('loanEntries')}</h4>
                <div className="space-y-2">
                    {getActiveLoanEntries(loan).length > 0 ? (
                        getActiveLoanEntries(loan).map((entry, index) => (
                            <div key={entry.id} className="bg-white rounded p-3 text-sm border border-gray-200">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex flex-wrap items-center gap-2 font-semibold text-lg text-gray-900">
                                        <span>{isRentalLoanEntry(entry) ? getRentalEntryLabel(entry) : getAdvanceEntryLabel(entry, index)}: {entry.amount} 円</span>
                                        <SubjectTypeBadge subjectType={getLoanEntrySubjectType(entry)} />
                                    </div>
                                    <span className="rounded-full bg-white px-2 py-1 text-xs text-gray-700 border border-gray-200">
                                        {normalizeLoanEntryCategory(entry.category ?? entry.Category)}
                                    </span>
                                </div>
                                <div className="text-gray-600">{t('loanDate')}: {formatDate(entry.loanDate)}</div>
                                <div className="text-gray-600">{t('plannedRepaymentEndDate')}: {formatDate(entry.plannedEndDate)}</div>
                                {entry.note && <div className="text-gray-500 text-xs mt-1">{entry.note}</div>}
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500">{t('none')}</p>
                    )}
                </div>
            </div>

            {/* Repayments */}
            <div>
                <h4 className="font-semibold text-md mb-3">{t('repaymentHistory')}</h4>
                <div className="space-y-2">
                    {loan.repayments && loan.repayments.length > 0 ? (
                        loan.repayments.map((repayment) => (
                            <div key={repayment.id} className="bg-white rounded p-3 text-sm flex justify-between items-center border border-gray-200">
                                <div className="flex items-center gap-2">
                                    <span>{formatDate(repayment.repaymentDate)}</span>
                                    <SubjectTypeBadge subjectType={getRepaymentSubjectType(repayment)} />
                                </div>
                                <span className="font-semibold">{repayment.amount} 円</span>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500">{t('none')}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

function SummaryPill({ label, breakdown, tone = 'slate', className = '' }) {
    const { t } = useTranslation();
    const accentClass = {
        rental: 'border-t-2 border-t-amber-500',
        advance: 'border-t-2 border-t-emerald-500',
        borrowed: 'border-t-2 border-t-sky-500',
        total: 'border-t-2 border-t-slate-500',
        slate: 'border-t-2 border-t-slate-400',
    }[tone] || 'border-t-2 border-t-slate-400';
    const rows = [
        { label: t('individualSubject'), value: breakdown.individual },
        { label: t('corporateSubject'), value: breakdown.corporate },
        { label: t('totalLabel'), value: breakdown.total, emphasize: true },
    ];

    return (
        <div className={`flex h-full min-h-[8.5rem] flex-col rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.06)] ring-1 ring-white ${accentClass} ${className}`}>
            <div className="text-xs font-medium text-gray-500">{label}</div>
            <div className="mt-3 space-y-2">
                {rows.map((row) => (
                    <div key={row.label} className="flex items-center justify-between gap-3">
                        <span className={`text-xs ${row.emphasize ? 'font-semibold text-gray-700' : 'text-gray-500'}`}>{row.label}</span>
                        <span className={`whitespace-nowrap text-sm ${row.emphasize ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}>
                            {formatYen(row.value)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function LoanSplitSection({
    loan,
    talentId,
    onUpdate,
    attachments,
    thumbnailUrls,
    loanEntryCategories,
    onCreateLoanEntryCategory,
    onDeleteAttachment,
    onDownloadAttachment,
    onPreviewAttachment,
    onUploadEntryAttachments,
    onCompleteLoan
}) {
    const { t } = useTranslation();
    const summary = getLoanFinancialSummary(loan);
    const breakdown = summary.subjectTotals || {
        individual: summary,
        corporate: {
            rentalBorrowed: 0,
            advanceBorrowed: 0,
            totalBorrowed: 0,
            rentalRepaid: 0,
            advanceRepaid: 0,
            totalRepaid: 0,
            rentalRemaining: 0,
            advanceRemaining: 0,
            totalRemaining: 0,
        },
        total: summary,
    };
    const canMarkAsCompleted = loan && summary.totalRemaining <= 0 && loan.status === 'Active' && summary.totalBorrowed > 0;
    const splitColumnsClass = 'grid grid-cols-1 gap-3 xl:grid-cols-[minmax(280px,0.382fr)_minmax(420px,0.618fr)]';

    return (
        <div className="space-y-5 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 shadow-inner">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{t('loanInfo')}</h3>
                        {loan && (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${loan.status === 'Active' ? 'bg-gray-900 text-white' : 'bg-gray-600 text-white'}`}>
                                {loan.status === 'Active' ? t('active') : t('completed')}
                            </span>
                        )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">賃貸は月ごとの完済チェック、立替金は金額入力で返済を管理します。</p>
                </div>
                {canMarkAsCompleted && (
                    <button
                        onClick={onCompleteLoan}
                        className="self-start rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700"
                    >
                        {t('markAsCompleted')}
                    </button>
                )}
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
                <SummaryPill
                    label="賃貸残高"
                    breakdown={{
                        individual: breakdown.individual.rentalRemaining,
                        corporate: breakdown.corporate.rentalRemaining,
                        total: breakdown.total.rentalRemaining,
                    }}
                    tone="rental"
                />
                <SummaryPill
                    label="立替金残高"
                    breakdown={{
                        individual: breakdown.individual.advanceRemaining,
                        corporate: breakdown.corporate.advanceRemaining,
                        total: breakdown.total.advanceRemaining,
                    }}
                    tone="advance"
                />
                <SummaryPill
                    label="総貸出額（賃貸除く）"
                    breakdown={{
                        individual: breakdown.individual.advanceBorrowed,
                        corporate: breakdown.corporate.advanceBorrowed,
                        total: breakdown.total.advanceBorrowed,
                    }}
                    tone="borrowed"
                />
                <SummaryPill
                    label="総残高"
                    breakdown={{
                        individual: breakdown.individual.totalRemaining,
                        corporate: breakdown.corporate.totalRemaining,
                        total: breakdown.total.totalRemaining,
                    }}
                    tone="total"
                />
            </div>

            <div className={`${splitColumnsClass} items-start`}>
                <RentalLoanPanel
                    loan={loan}
                    talentId={talentId}
                    onUpdate={onUpdate}
                    attachments={attachments}
                    thumbnailUrls={thumbnailUrls}
                    onDeleteAttachment={onDeleteAttachment}
                    onDownloadAttachment={onDownloadAttachment}
                    onPreviewAttachment={onPreviewAttachment}
                    onUploadEntryAttachments={onUploadEntryAttachments}
                />
                <AdvanceLoanPanel
                    loan={loan}
                    talentId={talentId}
                    onUpdate={onUpdate}
                    attachments={attachments}
                    thumbnailUrls={thumbnailUrls}
                    loanEntryCategories={loanEntryCategories}
                    onCreateLoanEntryCategory={onCreateLoanEntryCategory}
                    onDeleteAttachment={onDeleteAttachment}
                    onDownloadAttachment={onDownloadAttachment}
                    onPreviewAttachment={onPreviewAttachment}
                    onUploadEntryAttachments={onUploadEntryAttachments}
                />
            </div>
        </div>
    );
}

function RentalLoanPanel({
    loan,
    talentId,
    onUpdate,
    attachments,
    thumbnailUrls,
    onDeleteAttachment,
    onDownloadAttachment,
    onPreviewAttachment,
    onUploadEntryAttachments
}) {
    const { t } = useTranslation();
    const today = new Date().toISOString().split('T')[0];
    const defaultDate = loan?.loanEntries?.[0]?.loanDate?.split('T')[0] || today;
    const rentalEntries = getActiveLoanEntries(loan).filter(isRentalLoanEntry);
    const rentalRepayments = getLoanRepayments(loan).filter(isRentalRepayment);
    const [form, setForm] = useState({
        subjectType: LOAN_SUBJECT_TYPE.INDIVIDUAL,
        amount: '',
        loanDate: defaultDate,
        plannedEndDate: addMonthsEndOfMonth(defaultDate),
        note: ''
    });
    const [entryFiles, setEntryFiles] = useState([]);
    const [editingEntryId, setEditingEntryId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [pendingRepayment, setPendingRepayment] = useState({ entryId: null, date: today });
    const [editingRepayment, setEditingRepayment] = useState({ repaymentId: null, entryId: null, date: today });

    useEffect(() => {
        const nextDefaultDate = loan?.loanEntries?.[0]?.loanDate?.split('T')[0] || today;
        setForm({
            subjectType: LOAN_SUBJECT_TYPE.INDIVIDUAL,
            amount: '',
            loanDate: nextDefaultDate,
            plannedEndDate: addMonthsEndOfMonth(nextDefaultDate),
            note: ''
        });
        setEntryFiles([]);
        setEditingEntryId(null);
        setPendingRepayment({ entryId: null, date: today });
        setEditingRepayment({ repaymentId: null, entryId: null, date: today });
    }, [loan, today]);

    const resetForm = () => {
        setForm({
            subjectType: LOAN_SUBJECT_TYPE.INDIVIDUAL,
            amount: '',
            loanDate: today,
            plannedEndDate: addMonthsEndOfMonth(today),
            note: ''
        });
        setEntryFiles([]);
        setEditingEntryId(null);
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        if (name === 'loanDate') {
            setForm(prev => ({ ...prev, loanDate: value, plannedEndDate: addMonthsEndOfMonth(value) }));
            return;
        }
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveEntry = async () => {
        if (!form.amount || parseFloat(form.amount) <= 0) {
            alert(t('invalidInput'));
            return;
        }
        setIsSaving(true);
        const payload = {
            subjectType: normalizeLoanSubjectType(form.subjectType),
            amount: parseFloat(form.amount),
            category: RENTAL_CATEGORY,
            loanDate: form.loanDate,
            plannedEndDate: form.plannedEndDate,
            note: form.note
        };
        try {
            const savedEntry = editingEntryId
                ? await apiService.updateLoanEntry(talentId, editingEntryId, payload)
                : await apiService.addLoanEntry(talentId, payload);
            const targetEntryId = editingEntryId || savedEntry?.id || savedEntry?.Id;
            if (targetEntryId && entryFiles.length > 0) {
                await onUploadEntryAttachments(targetEntryId, entryFiles);
            }
            resetForm();
            await onUpdate();
        } catch (err) {
            alert(t('saveFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditEntry = (entry) => {
        setEditingEntryId(getEntryId(entry));
        setForm({
            subjectType: getLoanEntrySubjectType(entry),
            amount: entry.amount,
            loanDate: entry.loanDate?.split('T')[0] || entry.loanDate,
            plannedEndDate: entry.plannedEndDate?.split('T')[0] || entry.plannedEndDate,
            note: entry.note || ''
        });
        setEntryFiles([]);
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm(t('confirmDelete'))) return;
        setIsSaving(true);
        try {
            await apiService.deleteLoanEntry(talentId, entryId);
            await onUpdate();
        } catch (err) {
            alert(t('deleteFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmRepayment = async (entry) => {
        if (!loan?.id) return;
        const entryId = getEntryId(entry);
        setIsSaving(true);
        try {
            await apiService.addRepayment(loan.id, {
                category: REPAYMENT_CATEGORY.RENTAL,
                loanEntryId: entryId,
                amount: toNumber(entry.amount),
                subjectType: getLoanEntrySubjectType(entry),
                repaymentDate: pendingRepayment.date
            });
            setPendingRepayment({ entryId: null, date: today });
            await onUpdate();
        } catch (err) {
            alert(t('saveFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdateRepaymentDate = async (entry, repayment) => {
        if (!loan?.id) return;
        setIsSaving(true);
        try {
            await apiService.updateRepayment(loan.id, repayment.id, {
                category: REPAYMENT_CATEGORY.RENTAL,
                loanEntryId: getEntryId(entry),
                amount: toNumber(entry.amount),
                subjectType: getLoanEntrySubjectType(entry),
                repaymentDate: editingRepayment.date
            });
            setEditingRepayment({ repaymentId: null, entryId: null, date: today });
            await onUpdate();
        } catch (err) {
            alert(t('saveFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelRepayment = async (repaymentId) => {
        if (!loan?.id || !window.confirm(t('confirmDelete'))) return;
        setIsSaving(true);
        try {
            await apiService.deleteRepayment(loan.id, repaymentId);
            await onUpdate();
        } catch (err) {
            alert(t('deleteFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <section className={panelShellClass}>
            <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                    <h4 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                        <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true"></span>
                        賃貸
                    </h4>
                    <p className="mt-1 text-xs text-gray-500">月ごとの返済を登録します。</p>
                </div>
            </div>

            <div className={formCardClass}>
                <div className="mb-3 flex items-center justify-between">
                    <h5 className="border-l-2 border-sky-500 pl-2 text-sm font-semibold text-gray-900">賃貸借款を追加</h5>
                    {editingEntryId && (
                        <button onClick={resetForm} className="text-xs text-gray-600 underline">{t('cancel')}</button>
                    )}
                </div>
                <div className="grid grid-cols-1 gap-3">
                    <SelectItem
                        label={t('loanSubjectType')}
                        id="rental-loan-subject-type"
                        name="subjectType"
                        value={form.subjectType}
                        onChange={handleFormChange}
                        options={subjectTypeOptions}
                    />
                    <EditItem label={t('loanAmount')} name="amount" type="number" value={form.amount} onChange={handleFormChange} />
                    <EditItem label={t('loanDate')} name="loanDate" type="date" value={form.loanDate} onChange={handleFormChange} />
                    <EditItem label={t('plannedRepaymentEndDate')} name="plannedEndDate" type="date" value={form.plannedEndDate} onChange={handleFormChange} />
                    <EditItem label={t('loanEntryNote')} name="note" value={form.note} onChange={handleFormChange} />
                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
                        <label className="block text-sm font-medium text-gray-700">{t('loanEntryDocuments')}</label>
                        <label className="mt-1 inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                            <input type="file" multiple onChange={(e) => { setEntryFiles([...e.target.files]); e.target.value = ''; }} className="hidden" />
                            {t('selectFiles')}
                        </label>
                        <p className="mt-1 text-xs text-gray-500">
                            {entryFiles.length > 0 ? t('selectedFilesCount', { count: entryFiles.length }) : t('noFilesSelected')}
                        </p>
                    </div>
                </div>
                <div className="mt-3 flex justify-end">
                    <button type="button" onClick={handleSaveEntry} disabled={isSaving} className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:bg-gray-400">
                        {isSaving ? t('saving') : editingEntryId ? t('update') : t('addLoanEntry')}
                    </button>
                </div>
            </div>

            <div className={`mt-4 ${listShellClass}`}>
                <div className="max-h-[30rem] space-y-3 overflow-y-auto pr-1">
                {rentalEntries.length > 0 ? rentalEntries.map((entry) => {
                    const entryId = getEntryId(entry);
                    const repayment = rentalRepayments.find(r => getRepaymentLoanEntryId(r) === entryId);
                    const isPaid = Boolean(repayment);
                    const entryAttachments = (attachments || []).filter(att => getAttachmentLoanEntryId(att) === entryId);
                    const isPending = pendingRepayment.entryId === entryId;
                    const isEditingDate = editingRepayment.entryId === entryId;

                    return (
                        <div key={entryId} className={`${recordCardClass} border-l-4 border-l-amber-400`}>
                            <div className="flex items-start gap-3">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="rounded-full border border-amber-200 bg-white px-2 py-0.5 text-xs font-medium text-amber-700">{getRentalEntryLabel(entry)}</span>
                                            <SubjectTypeBadge subjectType={getLoanEntrySubjectType(entry)} />
                                            <span className="font-semibold text-gray-900">{formatYen(entry.amount)}</span>
                                            <span className={`rounded-full border bg-white px-2 py-0.5 text-xs font-medium ${isPaid ? 'border-emerald-200 text-emerald-700' : 'border-slate-200 text-slate-600'}`}>{isPaid ? '返済済み' : '未返済'}</span>
                                        </div>
                                        <div className="flex shrink-0 flex-wrap gap-2 text-xs">
                                            {!isPaid && !isPending && (
                                                <button
                                                    type="button"
                                                    onClick={() => setPendingRepayment({ entryId, date: today })}
                                                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50"
                                                >
                                                    賃貸返済を登録
                                                </button>
                                            )}
                                            {isPaid && !isEditingDate && (
                                                <button
                                                    type="button"
                                                    onClick={() => setEditingRepayment({ repaymentId: repayment.id, entryId, date: repayment.repaymentDate?.split('T')[0] || repayment.repaymentDate })}
                                                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50"
                                                >
                                                    返済日を修正
                                                </button>
                                            )}
                                            {isPaid && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleCancelRepayment(repayment.id)}
                                                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50"
                                                >
                                                    返済を取消
                                                </button>
                                            )}
                                            <button type="button" onClick={() => handleEditEntry(entry)} className="text-gray-700 hover:underline">{t('edit')}</button>
                                            <button type="button" onClick={() => handleDeleteEntry(entryId)} className="text-gray-700 hover:underline">{t('delete')}</button>
                                        </div>
                                    </div>
                                    <div className="mt-1 text-xs text-gray-500">{t('plannedRepaymentEndDate')}: {formatDateOnly(entry.plannedEndDate)}</div>
                                    {entry.note && <div className="mt-1 break-words text-xs text-gray-500">{entry.note}</div>}
                                    {!isPaid && !isPending && (
                                        <div className="mt-2 text-xs text-gray-500">返済ボタンを押すと返済日を入力できます</div>
                                    )}

                                    {isPending && (
                                        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 shadow-inner">
                                            <EditItem label={t('repaymentDate')} type="date" value={pendingRepayment.date} onChange={(e) => setPendingRepayment({ entryId, date: e.target.value })} />
                                            <div className="mt-3 flex justify-end gap-2">
                                                <button type="button" onClick={() => setPendingRepayment({ entryId: null, date: today })} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">{t('cancel')}</button>
                                                <button type="button" onClick={() => handleConfirmRepayment(entry)} className="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-700">{t('save')}</button>
                                            </div>
                                        </div>
                                    )}

                                    {isPaid && (
                                        <div className="mt-2 text-xs text-gray-600">
                                            {isEditingDate ? (
                                                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 shadow-inner">
                                                    <EditItem label={t('repaymentDate')} type="date" value={editingRepayment.date} onChange={(e) => setEditingRepayment({ repaymentId: repayment.id, entryId, date: e.target.value })} />
                                                    <div className="mt-3 flex justify-end gap-2">
                                                        <button type="button" onClick={() => setEditingRepayment({ repaymentId: null, entryId: null, date: today })} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700">{t('cancel')}</button>
                                                        <button type="button" onClick={() => handleUpdateRepaymentDate(entry, repayment)} className="rounded-md bg-gray-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-gray-700">{t('save')}</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex flex-wrap items-center justify-between gap-2">
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span>{t('repaymentDate')}: {formatDateOnly(repayment.repaymentDate)}</span>
                                                        <SubjectTypeBadge subjectType={getRepaymentSubjectType(repayment)} />
                                                    </div>
                                                    <button type="button" onClick={() => setEditingRepayment({ repaymentId: repayment.id, entryId, date: repayment.repaymentDate?.split('T')[0] || repayment.repaymentDate })} className="text-gray-700 hover:underline">{t('edit')}</button>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {entryAttachments.length > 0 && (
                                        <div className={attachmentAreaClass}>
                                            <LoanAttachmentList
                                                attachments={entryAttachments}
                                                thumbnailUrls={thumbnailUrls}
                                                onDelete={onDeleteAttachment}
                                                onDownload={onDownloadAttachment}
                                                onPreview={onPreviewAttachment}
                                                compact
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                }) : (
                    <p className="rounded-md border border-dashed border-gray-300 bg-white p-4 text-center text-sm text-gray-500">{t('none')}</p>
                )}
                </div>
            </div>
        </section>
    );
}

function AdvanceLoanPanel({
    loan,
    talentId,
    onUpdate,
    attachments,
    thumbnailUrls,
    loanEntryCategories = DEFAULT_ADVANCE_LOAN_ENTRY_CATEGORIES,
    onCreateLoanEntryCategory,
    onDeleteAttachment,
    onDownloadAttachment,
    onPreviewAttachment,
    onUploadEntryAttachments
}) {
    const { t } = useTranslation();
    const today = new Date().toISOString().split('T')[0];
    const defaultDate = loan?.loanEntries?.[0]?.loanDate?.split('T')[0] || today;
    const advanceCategories = buildAdvanceCategoryOptions(loanEntryCategories);
    const entries = getActiveLoanEntries(loan).filter(entry => !isRentalLoanEntry(entry));
    const repayments = getLoanRepayments(loan).filter(repayment => !isRentalRepayment(repayment));
    const summary = getLoanFinancialSummary(loan);
    const [entryForm, setEntryForm] = useState({
        subjectType: LOAN_SUBJECT_TYPE.INDIVIDUAL,
        category: DEFAULT_CATEGORY,
        amount: '',
        loanDate: defaultDate,
        plannedEndDate: addMonthsEndOfMonth(defaultDate),
        note: ''
    });
    const [entryFiles, setEntryFiles] = useState([]);
    const [editingEntryId, setEditingEntryId] = useState(null);
    const [amount, setAmount] = useState('');
    const [repaymentSubjectType, setRepaymentSubjectType] = useState(LOAN_SUBJECT_TYPE.INDIVIDUAL);
    const [repaymentDate, setRepaymentDate] = useState(today);
    const [editingRepaymentId, setEditingRepaymentId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const categoryOptions = buildAdvanceCategoryOptions([...advanceCategories, entryForm.category]);

    useEffect(() => {
        const nextDefaultDate = loan?.loanEntries?.[0]?.loanDate?.split('T')[0] || today;
        setEntryForm({
            subjectType: LOAN_SUBJECT_TYPE.INDIVIDUAL,
            category: DEFAULT_CATEGORY,
            amount: '',
            loanDate: nextDefaultDate,
            plannedEndDate: addMonthsEndOfMonth(nextDefaultDate),
            note: ''
        });
        setEntryFiles([]);
        setEditingEntryId(null);
        setAmount('');
        setRepaymentSubjectType(LOAN_SUBJECT_TYPE.INDIVIDUAL);
        setRepaymentDate(today);
        setEditingRepaymentId(null);
        setNewCategoryName('');
    }, [loan, today]);

    const handleEntryChange = (e) => {
        const { name, value } = e.target;
        if (name === 'loanDate') {
            setEntryForm(prev => ({ ...prev, loanDate: value, plannedEndDate: addMonthsEndOfMonth(value) }));
            return;
        }
        setEntryForm(prev => ({ ...prev, [name]: value }));
    };

    const resetEntryForm = () => {
        setEntryForm({
            subjectType: LOAN_SUBJECT_TYPE.INDIVIDUAL,
            category: DEFAULT_CATEGORY,
            amount: '',
            loanDate: today,
            plannedEndDate: addMonthsEndOfMonth(today),
            note: ''
        });
        setEntryFiles([]);
        setEditingEntryId(null);
        setNewCategoryName('');
    };

    const handleAddCategory = async () => {
        const name = newCategoryName.trim();
        if (!name) {
            alert('分類名を入力してください。');
            return;
        }

        if (!onCreateLoanEntryCategory) {
            alert(t('saveFailed'));
            return;
        }

        setIsAddingCategory(true);
        try {
            const createdName = await onCreateLoanEntryCategory(name);
            setEntryForm(prev => ({ ...prev, category: createdName }));
            setNewCategoryName('');
        } catch (err) {
            alert(t('saveFailed') + ': ' + err.message);
        } finally {
            setIsAddingCategory(false);
        }
    };

    const handleSaveEntry = async () => {
        if (!entryForm.amount || parseFloat(entryForm.amount) <= 0) {
            alert(t('invalidInput'));
            return;
        }
        setIsSaving(true);
        const payload = {
            subjectType: normalizeLoanSubjectType(entryForm.subjectType),
            amount: parseFloat(entryForm.amount),
            category: normalizeLoanEntryCategory(entryForm.category) === RENTAL_CATEGORY ? DEFAULT_CATEGORY : normalizeLoanEntryCategory(entryForm.category),
            loanDate: entryForm.loanDate,
            plannedEndDate: entryForm.plannedEndDate,
            note: entryForm.note
        };
        try {
            const savedEntry = editingEntryId
                ? await apiService.updateLoanEntry(talentId, editingEntryId, payload)
                : await apiService.addLoanEntry(talentId, payload);
            const targetEntryId = editingEntryId || savedEntry?.id || savedEntry?.Id;
            if (targetEntryId && entryFiles.length > 0) {
                await onUploadEntryAttachments(targetEntryId, entryFiles);
            }
            resetEntryForm();
            await onUpdate();
        } catch (err) {
            alert(t('saveFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditEntry = (entry) => {
        setEditingEntryId(getEntryId(entry));
        setEntryForm({
            subjectType: getLoanEntrySubjectType(entry),
            category: normalizeLoanEntryCategory(entry.category ?? entry.Category),
            amount: entry.amount,
            loanDate: entry.loanDate?.split('T')[0] || entry.loanDate,
            plannedEndDate: entry.plannedEndDate?.split('T')[0] || entry.plannedEndDate,
            note: entry.note || ''
        });
        setEntryFiles([]);
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm(t('confirmDelete'))) return;
        setIsSaving(true);
        try {
            await apiService.deleteLoanEntry(talentId, entryId);
            await onUpdate();
        } catch (err) {
            alert(t('deleteFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const resetRepaymentForm = () => {
        setAmount('');
        setRepaymentSubjectType(LOAN_SUBJECT_TYPE.INDIVIDUAL);
        setRepaymentDate(today);
        setEditingRepaymentId(null);
    };

    const handleSaveRepayment = async () => {
        if (!loan?.id || !amount || parseFloat(amount) <= 0) {
            alert(t('invalidInput'));
            return;
        }
        setIsSaving(true);
        const payload = {
            amount: parseFloat(amount),
            repaymentDate,
            category: REPAYMENT_CATEGORY.ADVANCE,
            subjectType: normalizeLoanSubjectType(repaymentSubjectType)
        };
        try {
            if (editingRepaymentId) {
                await apiService.updateRepayment(loan.id, editingRepaymentId, payload);
            } else {
                await apiService.addRepayment(loan.id, payload);
            }
            resetRepaymentForm();
            await onUpdate();
        } catch (err) {
            alert(t('saveFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditRepayment = (repayment) => {
        setEditingRepaymentId(repayment.id);
        setAmount(repayment.amount);
        setRepaymentSubjectType(getRepaymentSubjectType(repayment));
        setRepaymentDate(repayment.repaymentDate?.split('T')[0] || repayment.repaymentDate);
    };

    const handleDeleteRepayment = async (repaymentId) => {
        if (!loan?.id || !window.confirm(t('confirmDelete'))) return;
        setIsSaving(true);
        try {
            await apiService.deleteRepayment(loan.id, repaymentId);
            resetRepaymentForm();
            await onUpdate();
        } catch (err) {
            alert(t('deleteFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <section className={panelShellClass}>
            <div className="mb-4 flex items-start justify-between gap-3">
                <div>
                    <h4 className="flex items-center gap-2 text-base font-semibold text-gray-900">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true"></span>
                        立替金
                    </h4>
                    <p className="mt-1 text-xs text-gray-500">賃貸以外の借款を金額で返済します。</p>
                </div>
                <div className="rounded-lg bg-white px-3 py-2 text-right text-xs text-gray-500 ring-1 ring-gray-200">
                    <div>{ADVANCE_REPAYMENT_CATEGORY}残高</div>
                    <div className="text-sm font-semibold text-gray-900">{formatYen(summary.advanceRemaining)}</div>
                </div>
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.72fr)]">
                <div className={formCardClass}>
                    <div className="mb-3 flex items-center justify-between">
                        <h5 className="border-l-2 border-sky-500 pl-2 text-sm font-semibold text-gray-900">立替金借款を追加</h5>
                        {editingEntryId && <button onClick={resetEntryForm} className="text-xs text-gray-600 underline">{t('cancel')}</button>}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                            <label htmlFor="advance-entry-category" className="block text-sm font-medium text-gray-700">{t('loanEntryCategory')}</label>
                            <select
                                id="advance-entry-category"
                                name="category"
                                value={entryForm.category}
                                onChange={handleEntryChange}
                                className="mt-1 block w-full rounded-md border-gray-300 px-3 py-2 shadow-sm focus:border-gray-500 focus:outline-none focus:ring-gray-500"
                            >
                                {categoryOptions.map(category => <option key={category} value={category}>{category}</option>)}
                            </select>
                            <div className="mt-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-2">
                                <label htmlFor="new-advance-entry-category" className="block text-xs font-semibold text-slate-600">新しい分類を追加</label>
                                <div className="mt-1 flex flex-col gap-2 sm:flex-row">
                                    <input
                                        id="new-advance-entry-category"
                                        type="text"
                                        value={newCategoryName}
                                        maxLength={80}
                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleAddCategory();
                                            }
                                        }}
                                        placeholder="例: 交通費"
                                        className="block min-w-0 flex-1 rounded-md border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-gray-500 focus:outline-none focus:ring-gray-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddCategory}
                                        disabled={isAddingCategory}
                                        className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:bg-slate-100 disabled:text-slate-400"
                                    >
                                        {isAddingCategory ? '追加中...' : '分類を追加'}
                                    </button>
                                </div>
                                <p className="mt-1 text-xs text-slate-500">追加した分類は保存され、次回以降もプルダウンに表示されます。</p>
                            </div>
                        </div>
                        <EditItem label={t('loanAmount')} name="amount" type="number" value={entryForm.amount} onChange={handleEntryChange} />
                        <SelectItem
                            label={t('loanSubjectType')}
                            id="advance-loan-subject-type"
                            name="subjectType"
                            value={entryForm.subjectType}
                            onChange={handleEntryChange}
                            options={subjectTypeOptions}
                        />
                        <EditItem label={t('loanDate')} name="loanDate" type="date" value={entryForm.loanDate} onChange={handleEntryChange} />
                        <EditItem label={t('plannedRepaymentEndDate')} name="plannedEndDate" type="date" value={entryForm.plannedEndDate} onChange={handleEntryChange} />
                        <EditItem label={t('loanEntryNote')} name="note" value={entryForm.note} onChange={handleEntryChange} />
                        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
                            <label className="block text-sm font-medium text-gray-700">{t('loanEntryDocuments')}</label>
                            <label className="mt-1 inline-flex cursor-pointer items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">
                                <input type="file" multiple onChange={(e) => { setEntryFiles([...e.target.files]); e.target.value = ''; }} className="hidden" />
                                {t('selectFiles')}
                            </label>
                            <p className="mt-1 text-xs text-gray-500">
                                {entryFiles.length > 0 ? t('selectedFilesCount', { count: entryFiles.length }) : t('noFilesSelected')}
                            </p>
                        </div>
                    </div>
                    <div className="mt-3 flex justify-end">
                        <button type="button" onClick={handleSaveEntry} disabled={isSaving} className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:bg-gray-400">
                            {isSaving ? t('saving') : editingEntryId ? t('update') : t('addLoanEntry')}
                        </button>
                    </div>
                </div>

                <div className={formCardClass}>
                    <div className="mb-3 flex items-center justify-between">
                        <h5 className="border-l-2 border-violet-500 pl-2 text-sm font-semibold text-gray-900">{editingRepaymentId ? `${t('edit')} ${t('repaymentStatus')}` : `${t('create')} ${t('repaymentStatus')}`}</h5>
                        {editingRepaymentId && <button onClick={resetRepaymentForm} className="text-xs text-gray-600 underline">{t('cancel')}</button>}
                    </div>
                    <div className="space-y-3">
                        <EditItem label={t('repaymentAmount')} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
                        <SelectItem
                            label={t('repaymentSubjectType')}
                            id="advance-repayment-subject-type"
                            name="repaymentSubjectType"
                            value={repaymentSubjectType}
                            onChange={(e) => setRepaymentSubjectType(e.target.value)}
                            options={subjectTypeOptions}
                        />
                        <EditItem label={t('repaymentDate')} type="date" value={repaymentDate} onChange={(e) => setRepaymentDate(e.target.value)} />
                        <button onClick={handleSaveRepayment} disabled={isSaving || !loan?.id} className="w-full rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:bg-gray-400">
                            {isSaving ? t('saving') : t('save')}
                        </button>
                    </div>
                </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(260px,0.72fr)]">
                <div className={`max-h-[30rem] space-y-3 overflow-y-auto pr-1 ${listShellClass}`}>
                    {entries.length > 0 ? entries.map((entry) => {
                        const entryId = getEntryId(entry);
                        const entryAttachments = (attachments || []).filter(att => getAttachmentLoanEntryId(att) === entryId);
                        const category = normalizeLoanEntryCategory(entry.category ?? entry.Category);
                        return (
                        <div key={entryId} className={`${recordCardClass} border-l-4 border-l-sky-400`}>
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="rounded-full border border-sky-200 bg-white px-2.5 py-1 text-xs font-medium text-sky-700">{category}</span>
                                            <SubjectTypeBadge subjectType={getLoanEntrySubjectType(entry)} />
                                            <span className="font-semibold text-gray-900">{formatYen(entry.amount)}</span>
                                        </div>
                                        <div className="mt-2 grid grid-cols-1 gap-1 text-gray-600 sm:grid-cols-2">
                                            <div>{t('loanDate')}: {formatDateOnly(entry.loanDate)}</div>
                                            <div>{t('plannedRepaymentEndDate')}: {formatDateOnly(entry.plannedEndDate)}</div>
                                        </div>
                                        {entry.note && <div className="mt-2 break-words text-xs text-gray-500">{entry.note}</div>}
                                    </div>
                                    <div className="flex shrink-0 gap-2">
                                        <button onClick={() => handleEditEntry(entry)} className="text-xs text-gray-700 hover:underline">{t('edit')}</button>
                                        <button onClick={() => handleDeleteEntry(entryId)} className="text-xs text-gray-700 hover:underline">{t('delete')}</button>
                                    </div>
                                </div>
                                {entryAttachments.length > 0 && (
                                    <div className={attachmentAreaClass}>
                                        <LoanAttachmentList
                                            attachments={entryAttachments}
                                            thumbnailUrls={thumbnailUrls}
                                            onDelete={onDeleteAttachment}
                                            onDownload={onDownloadAttachment}
                                            onPreview={onPreviewAttachment}
                                            compact
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    }) : (
                        <p className="rounded-md border border-dashed border-gray-300 bg-white p-4 text-center text-sm text-gray-500">{t('none')}</p>
                    )}
                </div>

                <div className={formCardClass}>
                    <h5 className="mb-2 border-l-2 border-violet-500 pl-2 text-sm font-semibold text-gray-900">{t('repaymentHistory')}</h5>
                    <div className="max-h-[30rem] space-y-2 overflow-y-auto pr-1">
                        {repayments.length > 0 ? repayments.map(repayment => (
                            <div key={repayment.id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 border-l-4 border-l-violet-400 bg-white px-3 py-2 text-sm shadow-sm">
                                <div>
                                    <div className="flex flex-wrap items-center gap-2 font-medium text-gray-900">
                                        <span>{formatYen(repayment.amount)}</span>
                                        <SubjectTypeBadge subjectType={getRepaymentSubjectType(repayment)} />
                                    </div>
                                    <div className="text-xs text-gray-500">{formatDateOnly(repayment.repaymentDate)}</div>
                                </div>
                                <div className="flex shrink-0 gap-2">
                                    <button onClick={() => handleEditRepayment(repayment)} className="text-xs text-gray-700 hover:underline">{t('edit')}</button>
                                    <button onClick={() => handleDeleteRepayment(repayment.id)} className="text-xs text-gray-700 hover:underline">{t('delete')}</button>
                                </div>
                            </div>
                        )) : (
                            <p className="text-sm text-gray-500">{t('none')}</p>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

// Loan Information Component
// eslint-disable-next-line no-unused-vars
function LoanSection({
    loan,
    talentId,
    onUpdate,
    attachments,
    thumbnailUrls,
    onDeleteAttachment,
    onDownloadAttachment,
    onPreviewAttachment,
    onUploadEntryAttachments,
    onCompleteLoan
}) {
    const { t } = useTranslation();
    const today = new Date().toISOString().split('T')[0];
    const [entryForm, setEntryForm] = useState({
        category: DEFAULT_CATEGORY,
        amount: '',
        loanDate: today,
        plannedEndDate: addMonthsEndOfMonth(today),
        note: ''
    });
    const [entryFiles, setEntryFiles] = useState([]);
    const [editingEntryId, setEditingEntryId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const defaultDate = loan?.loanEntries?.[0]?.loanDate?.split('T')[0] || today;
        setEntryForm({
            category: DEFAULT_CATEGORY,
            amount: '',
            loanDate: defaultDate,
            plannedEndDate: addMonthsEndOfMonth(defaultDate),
            note: ''
        });
        setEntryFiles([]);
        setEditingEntryId(null);
    }, [loan, today]);

    const entries = getActiveLoanEntries(loan);
    const linkedAttachments = attachments || [];
    const unlinkedAttachments = linkedAttachments.filter(att => !getAttachmentLoanEntryId(att));
    const { rental, advance } = getLoanCategoryTotals(loan);
    const totalRepaid = loan?.repayments?.reduce((sum, r) => sum + toNumber(r.amount), 0) || 0;
    const formatDate = formatDateOnly;
    const summaryEndLabel = loan
        ? `${formatDate(loan.repaymentEndDate)} (${t('autoCalculated')})`
        : 'N/A';

    const handleEntryChange = (e) => {
        const { name, value } = e.target;
        if (name === 'loanDate') {
            const nextEnd = addMonthsEndOfMonth(value);
            setEntryForm(prev => ({ ...prev, loanDate: value, plannedEndDate: nextEnd }));
        } else {
            setEntryForm(prev => ({ ...prev, [name]: value }));
        }
    };

    const resetEntryForm = () => {
        setEntryForm({
            category: DEFAULT_CATEGORY,
            amount: '',
            loanDate: today,
            plannedEndDate: addMonthsEndOfMonth(today),
            note: ''
        });
        setEntryFiles([]);
        setEditingEntryId(null);
    };

    const handleSaveEntry = async () => {
        if (!entryForm.amount || parseFloat(entryForm.amount) <= 0) {
            alert(t('invalidInput'));
            return;
        }
        setIsSaving(true);
        const payload = {
            amount: parseFloat(entryForm.amount),
            category: normalizeLoanEntryCategory(entryForm.category),
            loanDate: entryForm.loanDate,
            plannedEndDate: entryForm.plannedEndDate,
            note: entryForm.note
        };
        try {
            let savedEntry = null;
            if (editingEntryId) {
                savedEntry = await apiService.updateLoanEntry(talentId, editingEntryId, payload);
            } else {
                savedEntry = await apiService.addLoanEntry(talentId, payload);
            }

            const targetEntryId = editingEntryId || savedEntry?.id || savedEntry?.Id;
            let uploadError = null;
            if (!targetEntryId && entryFiles.length > 0) {
                uploadError = new Error('書類を紐付ける借款IDを取得できませんでした');
            } else if (targetEntryId && entryFiles.length > 0) {
                try {
                    await onUploadEntryAttachments(targetEntryId, entryFiles);
                } catch (err) {
                    uploadError = err;
                }
            }
            resetEntryForm();
            await onUpdate();
            if (uploadError) {
                alert(`${t('saveSuccess')}。${t('uploadFailed')}: ${uploadError.message}`);
            }
        } catch (err) {
            alert(t('saveFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditEntry = (entry) => {
        setEditingEntryId(entry.id);
        setEntryForm({
            category: normalizeLoanEntryCategory(entry.category ?? entry.Category),
            amount: entry.amount,
            loanDate: entry.loanDate?.split('T')[0] || entry.loanDate,
            plannedEndDate: entry.plannedEndDate?.split('T')[0] || entry.plannedEndDate,
            note: entry.note || ''
        });
        setEntryFiles([]);
    };

    const handleDeleteEntry = async (entryId) => {
        if (!window.confirm(t('confirmDelete'))) return;
        setIsSaving(true);
        try {
            await apiService.deleteLoanEntry(talentId, entryId);
            await onUpdate();
        } catch (err) {
            alert(t('deleteFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEntryFilesChange = (e) => {
        setEntryFiles([...e.target.files]);
        e.target.value = '';
    };

    const handleEntryDocumentUpload = async (entryId, fileList) => {
        const selectedFiles = Array.from(fileList || []);
        if (selectedFiles.length === 0) return;
        setIsSaving(true);
        try {
            const result = await onUploadEntryAttachments(entryId, selectedFiles);
            alert(`${t('uploadSuccess')} (${result.successCount}/${result.totalCount})`);
        } catch (err) {
            alert(t('uploadFailed') + ': ' + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const canMarkAsCompleted = loan && loan.remainingBalance <= 0 && loan.status === 'Active';

    return (
        <div className="space-y-6">
            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg">{t('loanInfo')}</h3>
                    {loan && (
                        <span
                            className={`px-3 py-1 rounded-full text-xs font-medium ${
                                loan.status === 'Active'
                                    ? 'bg-green-600 text-white'
                                    : 'bg-gray-600 text-white'
                            }`}
                        >
                            {loan.status === 'Active' ? t('active') : t('completed')}
                        </span>
                    )}
                </div>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    <DetailItem label={t('rentalLoanAmount')} value={rental} isCurrency />
                    <DetailItem label={t('advanceLoanAmount')} value={advance} isCurrency />
                    <DetailItem label={t('totalAmount')} value={loan?.totalAmount || rental + advance} isCurrency />
                    <DetailItem label={t('repaymentAmount')} value={totalRepaid} isCurrency />
                    <DetailItem label={t('remainingBalance')} value={loan?.remainingBalance || 0} isCurrency />
                    <DetailItem label={t('loanDate')} value={formatDate(loan?.loanDate)} />
                    <DetailItem label={t('repaymentStartDate')} value={formatDate(loan?.repaymentStartDate)} />
                    <DetailItem label={t('plannedRepaymentEndDate')} value={summaryEndLabel} />
                </dl>
                {canMarkAsCompleted && (
                    <div className="mt-4">
                        <button
                            onClick={onCompleteLoan}
                            className="w-full bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition-colors"
                        >
                            {t('markAsCompleted')}
                        </button>
                    </div>
                )}
            </div>

            <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-md">{t('loanEntries')}</h4>
                    {editingEntryId && (
                        <button onClick={resetEntryForm} className="text-xs text-gray-600 underline">
                            {t('cancel')}
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="loan-entry-category" className="block text-sm font-medium text-gray-700">{t('loanEntryCategory')}</label>
                        <select
                            id="loan-entry-category"
                            name="category"
                            value={entryForm.category}
                            onChange={handleEntryChange}
                            className="mt-1 block w-full border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            {LOAN_ENTRY_CATEGORIES.map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </div>
                    <EditItem label={t('loanAmount')} name="amount" type="number" value={entryForm.amount} onChange={handleEntryChange} />
                    <EditItem label={t('loanDate')} name="loanDate" type="date" value={entryForm.loanDate} onChange={handleEntryChange} />
                    <EditItem label={t('plannedRepaymentEndDate')} name="plannedEndDate" type="date" value={entryForm.plannedEndDate} onChange={handleEntryChange} />
                    <EditItem label={t('loanEntryNote')} name="note" value={entryForm.note} onChange={handleEntryChange} />
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('loanEntryDocuments')}</label>
                        <label className="mt-1 inline-flex cursor-pointer items-center justify-center rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100">
                            <input type="file" multiple onChange={handleEntryFilesChange} className="hidden" />
                            {t('selectFiles')}
                        </label>
                        <p className="mt-1 text-xs text-gray-500">
                            {entryFiles.length > 0 ? t('selectedFilesCount', { count: entryFiles.length }) : t('noFilesSelected')}
                        </p>
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={handleSaveEntry}
                        disabled={isSaving}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-green-400"
                    >
                        {isSaving ? t('saving') : editingEntryId ? t('update') : t('addLoanEntry')}
                    </button>
                </div>
                <div className="space-y-3 max-h-[34rem] overflow-y-auto pr-1">
                    {entries.length > 0 ? entries.map((entry, index) => {
                        const entryAttachments = linkedAttachments.filter(att => getAttachmentLoanEntryId(att) === entry.id);
                        const category = normalizeLoanEntryCategory(entry.category ?? entry.Category);
                        return (
                        <div key={entry.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-gray-900">{getEntryLabel(index)}</span>
                                        <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${category === RENTAL_CATEGORY ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}>
                                            {category}
                                        </span>
                                        <span className="font-semibold text-gray-900">{formatYen(entry.amount)}</span>
                                    </div>
                                    <div className="mt-2 grid grid-cols-1 gap-1 text-gray-600 sm:grid-cols-2">
                                        <div>{t('loanDate')}: {formatDate(entry.loanDate)}</div>
                                        <div>{t('plannedRepaymentEndDate')}: {formatDate(entry.plannedEndDate)}</div>
                                    </div>
                                    {entry.note && <div className="text-gray-500 text-xs mt-2 break-words">{entry.note}</div>}
                                </div>
                                <div className="flex shrink-0 gap-2">
                                    <button onClick={() => handleEditEntry(entry)} className="text-blue-600 hover:underline text-xs">{t('edit')}</button>
                                    <button onClick={() => handleDeleteEntry(entry.id)} className="text-red-600 hover:underline text-xs">{t('delete')}</button>
                                </div>
                            </div>

                            <div className="mt-4 border-t border-gray-200 pt-3">
                                <div className="mb-2 flex items-center justify-between gap-3">
                                    <h5 className="text-xs font-semibold text-gray-700">{t('loanEntryDocuments')}</h5>
                                    <label className="cursor-pointer rounded-md bg-white px-2.5 py-1.5 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-50">
                                        <input
                                            type="file"
                                            multiple
                                            className="hidden"
                                            onChange={(e) => {
                                                handleEntryDocumentUpload(entry.id, e.target.files);
                                                e.target.value = '';
                                            }}
                                        />
                                        {t('uploadAttachments')}
                                    </label>
                                </div>
                                <LoanAttachmentList
                                    attachments={entryAttachments}
                                    thumbnailUrls={thumbnailUrls}
                                    onDelete={onDeleteAttachment}
                                    onDownload={onDownloadAttachment}
                                    onPreview={onPreviewAttachment}
                                    compact
                                />
                            </div>
                        </div>
                    );
                    }) : (
                        <p className="text-sm text-gray-500 text-center py-2">{t('none')}</p>
                    )}
                </div>
            </div>

            {unlinkedAttachments.length > 0 && (
                <div className="border-t pt-4">
                    <h4 className="font-semibold text-md mb-3">{t('unlinkedLoanAttachments')}</h4>
                    <LoanAttachmentList
                        attachments={unlinkedAttachments}
                        thumbnailUrls={thumbnailUrls}
                        onDelete={onDeleteAttachment}
                        onDownload={onDownloadAttachment}
                        onPreview={onPreviewAttachment}
                    />
                </div>
            )}
        </div>
    );
}

// Repayment Registration Component
// eslint-disable-next-line no-unused-vars
function RepaymentSection({ loan, onUpdate, showBackButton, onBack }) {
    const { t } = useTranslation();
    const [amount, setAmount] = useState('');
    const [repaymentDate, setRepaymentDate] = useState(new Date().toISOString().split('T')[0]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingRepaymentId, setEditingRepaymentId] = useState(null);

    if (!loan || !loan.id) {
        return <p className="text-sm text-gray-500">{t('loanInfo')} {t('none')}</p>;
    }

    const totalRepaid = loan.repayments?.reduce((sum, r) => sum + r.amount, 0) || 0;

    const resetForm = () => {
        setAmount('');
        setRepaymentDate(new Date().toISOString().split('T')[0]);
        setEditingRepaymentId(null);
    };

    const handleSave = async () => {
        if (!amount || amount <= 0) {
            alert(t('invalidInput'));
            return;
        }
        const payload = { amount: parseFloat(amount), repaymentDate };
        setIsLoading(true);
        try {
            if (editingRepaymentId) {
                await apiService.updateRepayment(loan.id, editingRepaymentId, payload);
                alert(t('saveSuccess'));
            } else {
                await apiService.addRepayment(loan.id, payload);
                alert(t('saveSuccess'));
            }
            resetForm();
            onUpdate();
        } catch (err) {
            alert(t('saveFailed') + ': ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (repayment) => {
        setEditingRepaymentId(repayment.id);
        setAmount(repayment.amount);
        setRepaymentDate(repayment.repaymentDate?.split('T')[0] || repayment.repaymentDate);
    };

    const handleDelete = async (repaymentId) => {
        if (!window.confirm(t('confirmDelete'))) return;
        setIsLoading(true);
        try {
            await apiService.deleteRepayment(loan.id, repaymentId);
            resetForm();
            onUpdate();
        } catch (err) {
            alert(t('deleteFailed') + ': ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="border-t lg:border-t-0 lg:border-l lg:pl-8 pt-8 lg:pt-0">
            <h3 className="font-semibold text-lg mb-2">{t('repaymentStatus')}</h3>
            <div className="space-y-2 bg-gray-50 p-4 rounded-md mb-6">
                <DetailItem label={t('repaymentAmount')} value={totalRepaid} isCurrency />
                <DetailItem label="回数" value={`${loan.repayments?.length || 0} 回`} />
                <DetailItem label={t('remainingBalance')} value={loan.remainingBalance} isCurrency />
            </div>

            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-md">
                        {editingRepaymentId ? t('edit') + ' ' + t('repaymentStatus') : t('create') + ' ' + t('repaymentStatus')}
                    </h4>
                    {editingRepaymentId && (
                        <button
                            type="button"
                            onClick={resetForm}
                            className="text-sm text-gray-600 underline"
                        >
                            {t('cancel')}
                        </button>
                    )}
                </div>
                <EditItem label={t('repaymentAmount')} type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
                <EditItem label={t('repaymentDate')} type="date" value={repaymentDate} onChange={(e) => setRepaymentDate(e.target.value)} />
                <div className="flex justify-end pt-2">
                    <button onClick={handleSave} disabled={isLoading} className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:bg-green-400">
                        {isLoading ? t('saving') : t('save')}
                    </button>
                </div>
            </div>
            
            <div className="mt-6 border-t pt-4">
                <h4 className="font-semibold text-md mb-2">{t('repaymentHistory')}</h4>
                <ul className="space-y-1 max-h-40 overflow-y-auto">
                    {loan.repayments?.length > 0 ? (
                        loan.repayments.map(r => (
                            <li key={r.id} className="text-sm text-gray-600 flex items-center justify-between">
                                <span>{r.repaymentDate}: {r.amount} 円</span>
                                <div className="space-x-2">
                                    <button
                                        type="button"
                                        onClick={() => handleEditClick(r)}
                                        className="text-blue-600 hover:underline text-xs"
                                    >
                                        {t('edit')}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleDelete(r.id)}
                                        className="text-red-600 hover:underline text-xs"
                                    >
                                        {t('delete')}
                                    </button>
                                </div>
                            </li>
                        ))
                    ) : <p className="text-sm text-gray-500">{t('none')}</p>}
                </ul>
            </div>
        </div>
    );
}
